import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek } from "@/lib/format";
import { ColorPill, ProgressBar } from "@/components/StatusPill";
import { WeekSelect } from "@/components/WeekSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, MapPin, Users2, Wallet, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Sites() {
  const { settings, isAdmin, isManager } = useAuth();
  const cur = settings.currency || "£";
  const [week, setWeek] = useState(currentWeek());
  const [sites, setSites] = useState(null);
  const [managers, setManagers] = useState([]);
  const [siteDlg, setSiteDlg] = useState(false);
  const [budgetDlg, setBudgetDlg] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", address: "", weekly_default_budget: 500, manager_id: "", status: "active" });
  const [alloc, setAlloc] = useState("");

  const load = () => { setSites(null); api.get(`/sites?week=${week}`).then((r) => setSites(r.data)); };
  useEffect(() => { load(); }, [week]);
  useEffect(() => { if (isManager) api.get("/users").then((r) => setManagers(r.data.filter((u) => ["manager", "admin"].includes(u.role)))); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", code: "", address: "", weekly_default_budget: 500, manager_id: "", status: "active" }); setSiteDlg(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, code: s.code, address: s.address, weekly_default_budget: s.weekly_default_budget, manager_id: s.manager_id || "", status: s.status }); setSiteDlg(true); };

  const saveSite = async () => {
    try {
      const payload = { ...form, weekly_default_budget: parseFloat(form.weekly_default_budget), manager_id: form.manager_id || null };
      if (editing) await api.put(`/sites/${editing.id}`, payload);
      else await api.post("/sites", payload);
      toast.success("Home saved"); setSiteDlg(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const openBudget = (s) => { setBudgetDlg(s); setAlloc(s.allocation || s.weekly_default_budget); };
  const saveBudget = async () => {
    try {
      await api.post("/budgets", { site_id: budgetDlg.id, week_start: week, allocation: parseFloat(alloc) });
      toast.success("Budget allocated"); setBudgetDlg(null); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Homes</h1>
          <p className="text-slate-500 text-sm mt-1">Weekly allocation, spend and remaining balance per home.</p>
        </div>
        <div className="flex items-center gap-3">
          <WeekSelect value={week} onChange={setWeek} />
          {isAdmin && <Button data-testid="add-site-btn" onClick={openNew} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-1.5" /> Add Home</Button>}
        </div>
      </div>

      {sites === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {sites.map((s) => (
            <Card key={s.id} data-testid={`site-card-${s.code}`} className="border-border p-5 space-y-4 hover:-translate-y-[1px] transition-transform duration-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-head text-lg font-medium text-slate-900">{s.name}</h3>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{s.code}</span>
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {s.address || "No address"}</div>
                </div>
                <ColorPill color={s.status_color}>{s.pct_used}%</ColorPill>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Budget</div><div className="font-mono tabular-nums font-semibold text-slate-800">{money(s.allocation, cur)}</div></div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Spent</div><div className="font-mono tabular-nums font-semibold text-slate-800">{money(s.spent, cur)}</div></div>
                <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Remaining</div><div className="font-mono tabular-nums font-semibold text-emerald-700">{money(s.remaining, cur)}</div></div>
              </div>
              <ProgressBar pct={s.pct_used} color={s.status_color} />

              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-slate-400 flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> {s.staff_count} staff</span>
                {isManager && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`set-budget-${s.code}`} onClick={() => openBudget(s)}><Wallet className="h-3.5 w-3.5 mr-1" /> Set Budget</Button>
                    {isAdmin && <Button size="sm" variant="ghost" data-testid={`edit-site-${s.code}`} onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Site dialog */}
      <Dialog open={siteDlg} onOpenChange={setSiteDlg}>
        <DialogContent data-testid="site-dialog">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit Home" : "Add Home"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="site-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input data-testid="site-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Weekly Default ({cur})</Label><Input data-testid="site-budget" type="number" value={form.weekly_default_budget} onChange={(e) => setForm({ ...form, weekly_default_budget: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Address</Label><Input data-testid="site-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Manager</Label>
                <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Assign" /></SelectTrigger>
                  <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-site" onClick={saveSite} className="bg-[#0F172A] hover:bg-[#1E293B]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget dialog */}
      <Dialog open={!!budgetDlg} onOpenChange={(o) => !o && setBudgetDlg(null)}>
        <DialogContent data-testid="budget-dialog">
          <DialogHeader><DialogTitle className="font-head">Allocate weekly budget</DialogTitle></DialogHeader>
          {budgetDlg && <p className="text-sm text-slate-500">{budgetDlg.name} · selected week</p>}
          <div><Label>Allocation ({cur})</Label><Input data-testid="budget-amount" type="number" step="0.01" value={alloc} onChange={(e) => setAlloc(e.target.value)} className="mt-1.5 h-12 text-lg font-mono" /></div>
          <DialogFooter><Button data-testid="save-budget" onClick={saveBudget} className="bg-[#0F172A] hover:bg-[#1E293B]">Allocate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
